import axios from 'axios'
import _ from 'lodash'
import yn from 'yn'
import moment from 'moment'

/**
 * Native re-implementation of the @dolli-lite/revinate abandoned-cart tracker.
 *
 * Keeps a single flat cart object in memory and debounce-sends the whole cart
 * to Revinate's upsertAbandonedCart endpoint as the guest fills out the booking
 * form, so unfinished bookings can be recovered downstream.
 *
 * Replaces the package's React context + SWR + usehooks-ts + MobX machinery
 * with the repo's existing axios + lodash, so it runs on React 16 / CRA 1
 * without any toolchain changes.
 */

const ENABLED = yn(process.env.REACT_APP_COMPONENT_ENABLE_REVINATE)

const REVINATE_HOTEL_CODE = 'B5C3'

let eligible = false

let started = false

// While developing/testing we don't actually hit Revinate — we just log the
// payload that WOULD be sent. Set REACT_APP_REVINATE_DRY_RUN=false to send for real.
const DRY_RUN = process.env.REACT_APP_REVINATE_DRY_RUN !== 'false'

const BASE_URL = 'https://fusion-api.navisperformance.com'
const UPSERT_ABANDONED_CART_PATH = '/sca/upsertAbandonedCart/'

const CART_TTL_MINUTES = 60

export const DEFAULT_REVINATE_CART = {
	startTime: '',
	expireTime: '',
	cartid: '',
	bookingEngine: '',
	keyword: '',
	booked: false,
	abandoned: false,
	firstname: '',
	lastname: '',
	email: '',
	phone: '',
	address: '',
	zipcode: '',
	city: '',
	state: '',
	country: '',
	rate: '',
	adults: 0,
	children: 0,
	arrival: '',
	departure: '',
	unit: '',
}

const client = axios.create({ baseURL: BASE_URL })

let cart = { ...DEFAULT_REVINATE_CART }

function uuidv4() {
	if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
		return window.crypto.randomUUID()
	}
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
		// eslint-disable-next-line no-bitwise
		const random = (Math.random() * 16) | 0
		// eslint-disable-next-line no-bitwise
		const value = char === 'x' ? random : (random & 0x3) | 0x8
		return value.toString(16)
	})
}

function isUuid(value) {
	return typeof value === 'string' &&
		/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
}

const send = _.debounce(() => {
	const params = {
		acctNbr: process.env.REACT_APP_REVINATE_ACCOUNT_NUMBER,
		jsPass: process.env.REACT_APP_REVINATE_JS_PASS,
	}

	if (DRY_RUN) {
		// eslint-disable-next-line no-console
		console.log('[revinate][dry-run] would POST', UPSERT_ABANDONED_CART_PATH, { params, cart })
		return
	}

	client
		.post(UPSERT_ABANDONED_CART_PATH, cart, { params })
		.catch(error => {
			// eslint-disable-next-line no-console
			console.warn('[revinate] upsertAbandonedCart failed', error)
		})
}, 200)

function sendIfStarted() {
	if (!started) return
	send()
}

/**
 * Decide whether abandoned-cart tracking should run for the active hotel.
 * Tracking only happens when the component is enabled AND the hotel is B5C3.
 * Call this from the booking-form container once the hotel code is known.
 */
export function configureRevinate(hotelCode) {
	eligible = ENABLED && hotelCode === REVINATE_HOTEL_CODE
}

export function ensureRevinateSession({ cartId } = {}) {
	if (!eligible) return
	if (cart.cartid) return

	const now = moment.utc()

	cart = {
		...cart,
		cartid: isUuid(cartId) ? cartId : uuidv4(),
		startTime: now.toISOString(),
		expireTime: now.clone().add(CART_TTL_MINUTES, 'minutes').toISOString(),
		bookingEngine: 'Verb',
		keyword: '8559722012',
		booked: false,
		abandoned: false,
	}
	sendIfStarted()
}

export function updateRevinateCartField(fieldName, value) {
	if (!eligible) return

	if (!(fieldName in DEFAULT_REVINATE_CART)) {
		// eslint-disable-next-line no-console
		console.warn(`[revinate] Unrecognized field "${fieldName}" — skipping.`)
		return
	}

	ensureRevinateSession()

	if (cart[fieldName] === value) return

	cart = { ...cart, [fieldName]: value }

	if (!started && fieldName === 'email' && value) {
		started = true
	}

	sendIfStarted()
}

export function seedRevinateCart(fields) {
	if (!eligible || !fields) return
	Object.keys(fields).forEach(key => updateRevinateCartField(key, fields[key]))
}

export function markRevinateBooked() {
	updateRevinateCartField('booked', true)
}

export function flushRevinateCart() {
	if (!eligible) return
	send.flush()
}


// WEBPACK FOOTER //
// ./src/utilities/revinate/revinateCart.js